import React, {FC, useEffect, useState} from 'react'
import {ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {useAuth} from '../../auth'
import './MonthEndReports.css'
import {KTSVG} from '../../../../_metronic/helpers'
import MachineryReportTable, {type MachineryReportConfig} from './MachineryReportTable'
import {getCompanyList, getCompanyAdminList} from '../../Management/core/_requests'
import type {Vessel, Company, CompanyAdmin} from '../../Management/core/_models'
import MachineryStatusPdfModal from './MachineryStatusPdfModal'
import {generateMachineryStatusPdf} from './MachineryStatusPdfGenerator'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import {
  fetchAccessibleVesselsForCurrentUser,
  formatAccessibleVesselError,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'
import {
  searchAuxiliaryEngineReports,
  createAuxiliaryEngineReport,
  updateAuxiliaryEngineReport,
  deleteAuxiliaryEngineReport,
  searchFreshWaterGeneratorReports,
  createFreshWaterGeneratorReport,
  updateFreshWaterGeneratorReport,
  deleteFreshWaterGeneratorReport,
  searchBwtsReports,
  createBwtsReport,
  updateBwtsReport,
  deleteBwtsReport,
  getBwtsReportDefaults,
  searchPurifierReports,
  createPurifierReport,
  updatePurifierReport,
  deletePurifierReport,
  searchInertGasSystemReports,
  createInertGasSystemReport,
  updateInertGasSystemReport,
  deleteInertGasSystemReport,
  getInertGasSystemReportDefaults,
  searchCompressorSystemReports,
  createCompressorSystemReport,
  updateCompressorSystemReport,
  deleteCompressorSystemReport,
  getCompressorSystemReportDefaults,
  searchBoilerPerformanceReports,
  createBoilerPerformanceReport,
  updateBoilerPerformanceReport,
  deleteBoilerPerformanceReport,
  getAuxiliaryEngineReportDefaults,
  getFreshWaterGeneratorReportDefaults,
  getPurifierReportDefaults,
  getBoilerPerformanceReportDefaults,
} from '../core/_requests'

const currentYear = new Date().getFullYear()

// Shared badge colors — matches MachineryReportTable's getBadgeStyle palette
const BADGE_COLORS = [
  { bg: '#f1faff', color: '#009ef7', border: '#009ef7' },
  { bg: '#e8fff3', color: '#50cd89', border: '#50cd89' },
  { bg: '#f8f5ff', color: '#7239ea', border: '#7239ea' },
  { bg: '#fff8dd', color: '#ffc700', border: '#ffc700' },
  { bg: '#ffeee8', color: '#f1416c', border: '#f1416c' },
  { bg: '#eff2f5', color: '#181c32', border: '#181c32' },
]

/**
 * Renders a stacked numbered-badge column cell that combines the flat DB field
 * (base, index 0) with any additional replicas stored in extraEntriesJson.makeReplicas[fieldKey].
 */
const renderBoilerReplicaColumn = (row: any, fieldKey: string): React.ReactNode => {
  const base = row[fieldKey]
  const replicas: any[] = (() => {
    try {
      const parsed = JSON.parse(row.extraEntriesJson || 'null')
      const arr = parsed?.makeReplicas?.[fieldKey]
      return Array.isArray(arr) ? arr : []
    } catch {
      return []
    }
  })()
  const values: string[] = []
  if (base !== undefined && base !== null && base !== '') values.push(String(base))
  replicas.forEach((v: any) => {
    const val = v && typeof v === 'object' && 'value' in v ? v.value : v
    if (val !== undefined && val !== null && val !== '') values.push(String(val))
  })
  if (values.length === 0) return '-'
  return (
    <div className='d-flex flex-column gap-2'>
      {values.map((v, i) => {
        const c = BADGE_COLORS[i % BADGE_COLORS.length]
        return (
          <div
            key={i}
            style={{
              width: '100%',
              minHeight: '36px',
              backgroundColor: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: '6px',
              padding: '4px 8px 4px 28px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              position: 'relative',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              className='position-absolute badge badge-circle fw-bold'
              style={{
                top: '50%',
                left: '4px',
                transform: 'translateY(-50%)',
                width: '18px',
                height: '18px',
                fontSize: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                color: c.color,
                border: `1px solid ${c.border}`,
              }}
            >
              {i + 1}
            </span>
            <span className='fw-semibold fs-7 text-truncate' style={{color: c.color}}>
              {v}
            </span>
          </div>
        )
      })}
    </div>
  )
}

const YES_NO_OPTS = [
  {value: 'Yes', label: 'Yes'},
  {value: 'No', label: 'No'},
]

const YES_NO_NA_OPTS = [
  {value: 'Yes', label: 'Yes'},
  {value: 'No', label: 'No'},
  {value: 'N/A', label: 'N/A'},
]

// Re-usable set of fields for any purifier unit (HFO, DO, ME Luboil, AE Luboil).
const purifierEntryFields = () =>
  [
    {key: 'makeModel', label: 'Make / Model', type: 'text' as const, colSpan: 6},
    {
      key: 'goodWorkingCondition',
      label: 'In Good Working Condition (Yes/No)',
      type: 'select' as const,
      colSpan: 6,
      options: YES_NO_OPTS,
    },
    {
      key: 'pmsSparePartsAvailable',
      label: 'PMS Spare Parts Available (Yes/No)',
      type: 'select' as const,
      colSpan: 6,
      options: YES_NO_OPTS,
    },
    {
      key: 'specialToolsAvailable',
      label: 'Special Tools Available (Yes/No)',
      type: 'select' as const,
      colSpan: 6,
      options: YES_NO_OPTS,
    },
  ]

// ============================================================================
// REPORT CONFIGS
// ============================================================================

const auxiliaryEngineConfig: MachineryReportConfig = {
  reportKey: 'auxiliary-engine',
  title: 'Auxiliary Engine Performance Analysis',
  subtitle: 'View and manage auxiliary engine performance reports',
  carryForwardFields: ['aeMakeTypeKw'],
  carryForwardReplicaLabels: ['aeMakeTypeKw'],
  columns: [
    {key: 'aeMakeTypeKw', header: 'AE Make / Type / KW'},
    {key: 'dailyConsumptionVlsfoMgo', header: 'Daily Consumption (VLSFO/MGO)'},
    {key: 'ae1MaxLoad', header: 'Max Load at 80%'},
    {key: 'normalOpLoadNormalWeather', header: 'Normal Load (Normal)'},
    {key: 'normalOpLoadColdWeather', header: 'Normal Load (Cold)'},
    {key: 'noOfAeRunAtSea', header: 'No. of AE Run at Sea'},
  ],
  formFields: [
    {key: 'aeMakeTypeKw', label: 'Auxiliary Engine Make / Type / KW', type: 'text', colSpan: 6, section: 'Auxiliary Engine Details', replicatable: true, replicateButtonLabel: 'Add AE'},
    {
      key: 'dailyConsumptionVlsfoMgo',
      label: 'Daily Consumption of one AE (VLSFO/MGO)',
      type: 'text',
      colSpan: 6,
      section: 'Auxiliary Engine Details',
      replicatable: true,
    },
    {
      key: 'ae1MaxLoad',
      label: 'AE1 - Max Load presently taken / Last tried at 80% Load (Mention Date)',
      type: 'text',
      colSpan: 12,
      section: 'Max Load at 80%',
      replicatable: true,
      replicateButtonLabel: 'Add AE',
    },
    {key: 'normalOpLoadNormalWeather', label: 'Normal Operational Load at Sea - Normal Weather', type: 'text', colSpan: 6, section: 'Operational Load at Sea'},
    {key: 'normalOpLoadColdWeather', label: 'Normal Operational Load at Sea - Cold Weather', type: 'text', colSpan: 6, section: 'Operational Load at Sea'},
    {key: 'noOfAeRunAtSea', label: 'No of AE run at Sea (if more than one run for normal operational purpose, mention load on each)', type: 'textarea', colSpan: 12, section: ''},
    {key: 'comments', label: 'Comments (reasons if AE unable to run upto 80% of the rated cap and plans to improve AE performance)', type: 'textarea', colSpan: 12, section: ''},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    aeMakeTypeKw: '',
    dailyConsumptionVlsfoMgo: '',
    ae1MaxLoad: '',
    normalOpLoadNormalWeather: '',
    normalOpLoadColdWeather: '',
    noOfAeRunAtSea: '',
    comments: '',
  }),
  searchFn: searchAuxiliaryEngineReports,
  createFn: createAuxiliaryEngineReport,
  updateFn: updateAuxiliaryEngineReport,
  deleteFn: deleteAuxiliaryEngineReport,
  defaultsFn: getAuxiliaryEngineReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalAe',
      anchorSection: 'Auxiliary Engine Details',
      addButtonLabel: 'Add AE',
      itemLabelPrefix: 'AE',
      startIndex: 4,
      fields: [
        {key: 'aeMakeTypeKw', label: 'Make / Type / KW', type: 'text', colSpan: 6},
        {
          key: 'maxLoad',
          label: 'Max Load presently taken / Last tried at 80% Load (Mention Date)',
          type: 'text',
          colSpan: 6,
        },
      ],
    },
  ],
}

const freshWaterGeneratorConfig: MachineryReportConfig = {
  reportKey: 'fresh-water-generator',
  title: 'Fresh Water Generator System Performance',
  subtitle: 'View and manage fresh water generator performance reports',
  carryForwardFields: ['fwgMakeTypeCapacity'],
  carryForwardReplicaLabels: ['fwgMakeTypeCapacity'],
  columns: [
    {key: 'fwgMakeTypeCapacity', header: 'FWG Make / Type / Capacity'},
    {key: 'dateOfLastMaintenance', header: 'Date of Last Maintenance'},
    {key: 'maxActualFwgCapacityAtSea', header: 'Max Actual FWG Capacity at Sea'},
    {key: 'sparePartsOnOrder', header: 'Spare Parts on Order (Y/N)'},
  ],
  formFields: [
    {key: 'fwgMakeTypeCapacity', label: 'FWG Make / Type / Capacity', type: 'text', colSpan: 6, section: 'Fresh Water Generator Details', replicatable: true, replicateButtonLabel: 'Add FWG'},
    {key: 'dateOfLastMaintenance', label: 'Date of Last Maintenance', type: 'date', colSpan: 6, section: 'Fresh Water Generator Details', replicatable: true},
    {key: 'maxActualFwgCapacityAtSea', label: 'Max Actual FWG Capacity at Sea', type: 'text', colSpan: 6, section: 'Fresh Water Generator Details'},
    {key: 'sparePartsOnOrder', label: 'Spare Parts on Order (Y/N)', type: 'select', colSpan: 6, section: 'Fresh Water Generator Details', options: YES_NO_OPTS},
    {key: 'comments', label: 'Comments (reasons if FWG unable to generate water 75% of Designed Performance and action plans to improve Boiler performance)', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    fwgMakeTypeCapacity: '',
    maxActualFwgCapacityAtSea: '',
    dateOfLastMaintenance: '',
    sparePartsOnOrder: '',
    comments: '',
  }),
  searchFn: searchFreshWaterGeneratorReports,
  createFn: createFreshWaterGeneratorReport,
  updateFn: updateFreshWaterGeneratorReport,
  deleteFn: deleteFreshWaterGeneratorReport,
  defaultsFn: getFreshWaterGeneratorReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalFwg',
      anchorSection: 'Fresh Water Generator Details',
      addButtonLabel: 'Add FWG',
      itemLabelPrefix: 'FWG #',
      startIndex: 2,
      fields: [
        {key: 'fwgMakeTypeCapacity', label: 'Make / Type / Capacity', type: 'text', colSpan: 6},
        {
          key: 'dateOfLastMaintenance',
          label: 'Date of Last Maintenance',
          type: 'date',
          colSpan: 6,
        },
      ],
    },
  ],
}

const bwtsConfig: MachineryReportConfig = {
  reportKey: 'bwts',
  title: 'BWTS System Performance',
  subtitle: 'View and manage BWTS system performance reports',
  carryForwardFields: ['bwtsMakeTypeCapacity'],
  carryForwardReplicaLabels: ['bwtsMakeTypeCapacity'],
  columns: [
    {key: 'bwtsMakeTypeCapacity', header: 'BWTS Make / Type / Capacity'},
    {key: 'bwtsOperationalTestDatePassed', header: 'Operational Test Date / Passed Y/N'},
    {key: 'minStockCriticalSparePartsChecked', header: 'Min Stock Spare Parts Checked'},
    {key: 'dateOfLastShoreServiceAttendance', header: 'Last Shore Service'},
    {key: 'chemicalStockEnough', header: 'Chemical Stock Enough'},
  ],
  formFields: [
    {key: 'bwtsMakeTypeCapacity', label: 'BWTS Make / Type / Capacity', type: 'text', colSpan: 6, section: 'BWTS Details', replicatable: true, replicateButtonLabel: 'Add BWTS'},
    {key: 'bwtsOperationalTestDatePassed', label: 'BWTS Operational Test date / passed Y/N', type: 'text', colSpan: 6, section: 'BWTS Details', replicatable: true},
    {key: 'minStockCriticalSparePartsChecked', label: 'Minimum stock for Critical spare parts properly checked', type: 'select', colSpan: 6, options: YES_NO_OPTS, replicatable: true},
    {key: 'dateOfLastShoreServiceAttendance', label: 'Date of Last Shore Service Attendance', type: 'date', colSpan: 6, replicatable: true},
    {key: 'chemicalStockEnough', label: 'Chemical stock is enough to operate YES/NO (if not applicable - NA)', type: 'select', colSpan: 6, options: YES_NO_NA_OPTS},
    {key: 'comments', label: 'Comments (Please mention BWTS unable to run as Designed and plans to improve performance)', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    bwtsMakeTypeCapacity: '',
    bwtsOperationalTestDatePassed: '',
    chemicalStockEnough: '',
    minStockCriticalSparePartsChecked: '',
    dateOfLastShoreServiceAttendance: '',
    comments: '',
  }),
  searchFn: searchBwtsReports,
  createFn: createBwtsReport,
  updateFn: updateBwtsReport,
  deleteFn: deleteBwtsReport,
  defaultsFn: getBwtsReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalBwts',
      anchorSection: 'BWTS Details',
      addButtonLabel: 'Add BWTS',
      itemLabelPrefix: 'BWTS #',
      startIndex: 2,
      fields: [
        {key: 'bwtsMakeTypeCapacity', label: 'Make / Type / Capacity', type: 'text', colSpan: 6},
        {
          key: 'bwtsOperationalTestDatePassed',
          label: 'Operational Test date / Passed Y/N',
          type: 'text',
          colSpan: 6,
        },
      ],
    },
  ],
}

const purifierConfig: MachineryReportConfig = {
  reportKey: 'purifier',
  title: 'Purifier Performance',
  subtitle: 'View and manage purifier performance reports',
  carryForwardFields: ['hfoPurifier1MakeModel'],
  carryForwardReplicaLabels: ['hfoPurifier1MakeModel'],
  columns: [
    {key: 'hfoPurifier1MakeModel', header: 'HFO Purifier #1'},
    {key: 'hfoPurifier1GoodWorkingCondition', header: 'HFO #1 Working'},
    {key: 'hfoPurifier1PmsSparePartAvailable', header: 'HFO #1 Spare'},
  ],
  formFields: [
    {key: 'hfoPurifier1MakeModel', label: 'HFO Purifier #1 - Make / Model', type: 'text', colSpan: 6, section: 'HFO Purifier #1', replicatable: true, replicateButtonLabel: 'Add HFO Purifier'},
    {key: 'hfoPurifier1GoodWorkingCondition', label: 'In Good Working Condition (Yes/No)', type: 'select', colSpan: 12, section: 'HFO Purifier #1', options: YES_NO_OPTS, replicatable: true},
    {key: 'hfoPurifier1PmsSparePartAvailable', label: 'PMS Spare Part Available (Yes/No)', type: 'select', colSpan: 12, section: 'HFO Purifier #1', options: YES_NO_OPTS, replicatable: true},

    {key: 'comments', label: 'Comments', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    hfoPurifier1MakeModel: '', hfoPurifier1GoodWorkingCondition: '', hfoPurifier1PmsSparePartAvailable: '',
    comments: '',
  }),
  searchFn: searchPurifierReports,
  createFn: createPurifierReport,
  updateFn: updatePurifierReport,
  deleteFn: deletePurifierReport,
  defaultsFn: getPurifierReportDefaults,
}

const inertGasSystemConfig: MachineryReportConfig = {
  reportKey: 'inert-gas-system',
  title: 'Inert Gas System Performance',
  subtitle: 'View and manage inert gas system performance reports',
  carryForwardFields: ['igIgsMakeTypeCapacity'],
  carryForwardReplicaLabels: ['igIgsMakeTypeCapacity'],
  columns: [
    {key: 'igIgsMakeTypeCapacity', header: 'IG/IGS Make / Type / Capacity'},
    {key: 'dateOfLastSensorsCalibration', header: 'Last Sensors Calibration'},
    {key: 'igIgsPerformanceTestDatePassed', header: 'Performance Test / Passed Y/N'},
    {key: 'lastTimeDemisterCleaned', header: 'Last Demister Cleaned'},
    {key: 'dateOfLastShoreServiceAttendance', header: 'Last Shore Service'},
    {key: 'dateOfLastInternalInspection', header: 'Last Internal Inspection'},
    {key: 'dateOfLastWaterNodesCheck', header: 'Last Water Nodes Check'},
  ],
  formFields: [
    {key: 'igIgsMakeTypeCapacity', label: 'IG / IGS Make / Type / Capacity', type: 'text', colSpan: 12, section: 'Inert Gas System Details', replicatable: true, replicateButtonLabel: 'Add IG / IGS'},
    {key: 'igIgsPerformanceTestDatePassed', label: 'IG/IGS Performance Test date / passed Y/N', type: 'text', colSpan: 6, section: 'Inert Gas System Details', replicatable: true},
    {key: 'dateOfLastSensorsCalibration', label: 'Date of Last Sensors Calibration', type: 'date', colSpan: 6, replicatable: true},
    {key: 'lastTimeDemisterCleaned', label: 'Last time Demister cleaned', type: 'date', colSpan: 6, replicatable: true},
    {key: 'dateOfLastShoreServiceAttendance', label: 'Date of Last Shore Service Attendance', type: 'date', colSpan: 6, replicatable: true},
    {key: 'dateOfLastInternalInspection', label: 'Date of Last Internal Inspection', type: 'date', colSpan: 6, replicatable: true},
    {key: 'dateOfLastWaterNodesCheck', label: 'Date of Last Water Nodes Check', type: 'date', colSpan: 6, replicatable: true},
    {key: 'comments', label: 'Comments (Please mention reasons IG or IGS unable to run up to Designed Performance and plans to improve performance)', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    igIgsMakeTypeCapacity: '',
    dateOfLastSensorsCalibration: '',
    igIgsPerformanceTestDatePassed: '',
    lastTimeDemisterCleaned: '',
    dateOfLastShoreServiceAttendance: '',
    dateOfLastInternalInspection: '',
    dateOfLastWaterNodesCheck: '',
    comments: '',
  }),
  searchFn: searchInertGasSystemReports,
  createFn: createInertGasSystemReport,
  updateFn: updateInertGasSystemReport,
  deleteFn: deleteInertGasSystemReport,
  defaultsFn: getInertGasSystemReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalIgs',
      anchorSection: 'Inert Gas System Details',
      addButtonLabel: 'Add IG/IGS',
      itemLabelPrefix: 'IG/IGS #',
      startIndex: 2,
      fields: [
        {key: 'igIgsMakeTypeCapacity', label: 'Make / Type / Capacity', type: 'text', colSpan: 6},
        {
          key: 'igIgsPerformanceTestDatePassed',
          label: 'Performance Test date / Passed Y/N',
          type: 'text',
          colSpan: 6,
        },
      ],
    },
  ],
}

const compressorSystemConfig: MachineryReportConfig = {
  reportKey: 'compressor-system',
  title: 'Compressor System Performance',
  subtitle: 'View and manage compressor system performance reports',
  carryForwardFields: ['mac1MakeModelCapacity'],
  carryForwardReplicaLabels: ['mac1MakeModelCapacity'],
  columns: [
    {key: 'mac1MakeModelCapacity', header: 'MAC #1'},
    {key: 'mac1GoodWorkingCondition', header: 'MAC #1 Working'},
    {key: 'mac1PmsSparePartAvailable', header: 'MAC #1 Spare'},
  ],
  formFields: [
    {key: 'mac1MakeModelCapacity', label: 'MAC #1 - Make / Model / Capacity', type: 'text', colSpan: 12, section: 'Main Air Compressor #1', replicatable: true, replicateButtonLabel: 'Add MAC'},
    {key: 'mac1GoodWorkingCondition', label: 'In Good Working Condition (Yes/No)', type: 'select', colSpan: 12, section: 'Main Air Compressor #1', options: YES_NO_OPTS, replicatable: true},
    {key: 'mac1PmsSparePartAvailable', label: 'PMS Spare Part Available (Yes/No)', type: 'select', colSpan: 12, section: 'Main Air Compressor #1', options: YES_NO_OPTS, replicatable: true},

    {key: 'comments', label: 'Comments (Please mention if any Air Compressor unable to run as Designed and plans to improve performance)', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    mac1MakeModelCapacity: '', mac1GoodWorkingCondition: '', mac1PmsSparePartAvailable: '',
    comments: '',
  }),
  searchFn: searchCompressorSystemReports,
  createFn: createCompressorSystemReport,
  updateFn: updateCompressorSystemReport,
  deleteFn: deleteCompressorSystemReport,
  defaultsFn: getCompressorSystemReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalMac',
      anchorSection: 'Main Air Compressor #2',
      addButtonLabel: 'Add MAC',
      itemLabelPrefix: 'MAC #',
      startIndex: 3,
      fields: [
        {key: 'makeModelCapacity', label: 'Make / Model / Capacity', type: 'text', colSpan: 12},
        {
          key: 'goodWorkingCondition',
          label: 'In Good Working Condition (Yes/No)',
          type: 'select',
          colSpan: 6,
          options: YES_NO_OPTS,
        },
        {
          key: 'pmsSparePartAvailable',
          label: 'PMS Spare Part Available (Yes/No)',
          type: 'select',
          colSpan: 6,
          options: YES_NO_OPTS,
        },
      ],
    },
    {
      sectionId: 'additionalServiceDeckComp',
      anchorSection: 'Service / Deck Air Compressor',
      addButtonLabel: 'Add Service/Deck Compressor',
      itemLabelPrefix: 'Service/Deck Compressor #',
      startIndex: 2,
      fields: [
        {key: 'makeModelCapacity', label: 'Make / Model / Capacity', type: 'text', colSpan: 12},
        {
          key: 'goodWorkingCondition',
          label: 'In Good Working Condition (Yes/No)',
          type: 'select',
          colSpan: 6,
          options: YES_NO_OPTS,
        },
        {
          key: 'pmsSparePartAvailable',
          label: 'PMS Spare Part Available (Yes/No)',
          type: 'select',
          colSpan: 6,
          options: YES_NO_OPTS,
        },
      ],
    },
  ],
}

const boilerPerformanceConfig: MachineryReportConfig = {
  reportKey: 'boiler-performance',
  title: 'Boiler Performance Analysis',
  subtitle: 'View and manage boiler performance analysis reports',
  carryForwardFields: ['boilerMakeTypeCapacity'],
  carryForwardReplicaLabels: ['boilerMakeTypeCapacity'],
  columns: [
    {key: 'boilerMakeTypeCapacity', header: 'Boiler Make / Type / Capacity'},
    {
      key: 'boiler1PerformanceTestDatePressure',
      header: 'Performance Test / Pressure',
      render: (row: any) => renderBoilerReplicaColumn(row, 'boiler1PerformanceTestDatePressure'),
    },
    {
      key: 'waterAnalysisOk',
      header: 'Water Analysis OK',
      render: (row: any) => renderBoilerReplicaColumn(row, 'waterAnalysisOk'),
    },
    {key: 'enoughStockOfChemicalForAnalysis', header: 'Enough Chemical Stock'},
    {
      key: 'quantityPluggedBoiler1Pipes',
      header: 'Plugged Pipes',
      render: (row: any) => renderBoilerReplicaColumn(row, 'quantityPluggedBoiler1Pipes'),
    },
  ],
  formFields: [
    {key: 'boilerMakeTypeCapacity', label: 'Boiler Make / Type / Capacity', type: 'text', colSpan: 12, section: 'Boiler Details', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'boiler1PerformanceTestDatePressure', label: 'Boiler #1 Performance Test date / Pressure', type: 'text', colSpan: 6, section: 'Performance Tests', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'dateOfLastBoilerWaterAnalysis', label: 'Date of Last Boiler Water Analysis', type: 'date', colSpan: 6, section: 'Boiler Water Analysis', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'waterAnalysisOk', label: 'Water Analysis OK (Yes/Not), if Not corrective action', type: 'text', colSpan: 6, section: 'Boiler Water Analysis', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'enoughStockOfChemicalForAnalysis', label: 'Enough Stock of Chemical for Analysis (Yes/Not)', type: 'select', colSpan: 6, section: 'Boiler Water Analysis', options: YES_NO_OPTS, replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'lastDateBoiler1InternalInspection', label: 'Last Date of Boiler #1 Internal Inspection', type: 'date', colSpan: 6, section: 'Internal Inspections', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'quantityPluggedBoiler1Pipes', label: 'Quantity of Plugged Boiler #1 Pipes', type: 'text', colSpan: 6, section: 'Plugged Pipes', replicatable: true, replicateButtonLabel: 'Add Boiler'},
    {key: 'comments', label: 'Comments', type: 'textarea', colSpan: 12},
  ],
  emptyForm: () => ({
    vesselId: null,
    reportMonth: new Date().getMonth() + 1,
    reportYear: currentYear,
    boilerMakeTypeCapacity: '',
    boiler1PerformanceTestDatePressure: '', boiler2PerformanceTestDatePressure: '',
    dateOfLastBoilerWaterAnalysis: '', waterAnalysisOk: '', enoughStockOfChemicalForAnalysis: '',
    lastDateBoiler1InternalInspection: '', lastDateBoiler2InternalInspection: '',
    quantityPluggedBoiler1Pipes: '', quantityPluggedBoiler2Pipes: '',
    comments: '',
  }),
  searchFn: searchBoilerPerformanceReports,
  createFn: createBoilerPerformanceReport,
  updateFn: updateBoilerPerformanceReport,
  deleteFn: deleteBoilerPerformanceReport,
  defaultsFn: getBoilerPerformanceReportDefaults,
  repeatableSections: [
    {
      sectionId: 'additionalBoilers',
      anchorSection: 'Boiler Details',
      addButtonLabel: 'Add Boiler',
      itemLabelPrefix: 'Boiler #',
      startIndex: 3,
      fields: [
        {
          key: 'makeTypeCapacity',
          label: 'Make / Type / Capacity',
          type: 'text',
          colSpan: 12,
        },
        {
          key: 'performanceTestDatePressure',
          label: 'Performance Test date / Pressure',
          type: 'text',
          colSpan: 6,
        },
        {
          key: 'lastInternalInspection',
          label: 'Last Date of Internal Inspection',
          type: 'date',
          colSpan: 6,
        },
        {
          key: 'quantityPluggedPipes',
          label: 'Quantity of Plugged Pipes',
          type: 'text',
          colSpan: 12,
        },
      ],
    },
  ],
}

// ============================================================================
// REPORT NAV ITEMS (order: Auxiliary Engine → Boiler → Compressor → Purifier → Inert Gas → BWTS → Fresh Water Generator)
// ============================================================================

type ReportItem = {
  key: string
  label: string
  icon: string
  config: MachineryReportConfig
}

const REPORTS: ReportItem[] = [
  {key: 'auxiliary-engine', label: 'Auxiliary Engine', icon: 'bi-lightning-charge', config: auxiliaryEngineConfig},
  {key: 'boiler-performance', label: 'Boiler', icon: 'bi-thermometer-half', config: boilerPerformanceConfig},
  {key: 'compressor-system', label: 'Compressor', icon: 'bi-fan', config: compressorSystemConfig},
  {key: 'purifier', label: 'Purifier', icon: 'bi-funnel', config: purifierConfig},
  {key: 'inert-gas-system', label: 'Inert Gas', icon: 'bi-cloud', config: inertGasSystemConfig},
  {key: 'bwts', label: 'BWTS', icon: 'bi-water', config: bwtsConfig},
  {key: 'fresh-water-generator', label: 'Fresh Water Generator', icon: 'bi-droplet-half', config: freshWaterGeneratorConfig},
]

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MachineryStatusPage: FC = () => {
  const {currentUser} = useAuth()
  const {canRead, canCreate, hasAssignedVessel, isGlobalScope} =
    useMonthEndFeatureAccess('machinery-status')
  const isShipboardUser = hasAssignedVessel
  const canAddMachineryReport = canCreate
  const accessDenied = !canRead

  /* if (!canRead) {
    return (
      <QhseAccessDenied message='Access denied — you do not have permission to view Machinery Reports.' />
    )
  } */

  const [activeReport, setActiveReport] = useState<string>(REPORTS[0].key)
  const [modalReportKey, setModalReportKey] = useState<string>(REPORTS[0].key)
  const [modalSwitchSeed, setModalSwitchSeed] = useState<{
    vesselId?: number | null
    reportYear?: number | null
    reportMonth?: number | null
  } | null>(null)
  const [addRequest, setAddRequest] = useState<{
    id: number
    seed?: {vesselId?: number | null; reportYear?: number | null; reportMonth?: number | null}
  }>({ id: 0 })

  // PDF modal state (no longer needed for direct download, but keeping for potential future use)
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false)

  // Vessel / company data for PDF modal
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<Company[]>([])

  useEffect(() => {
    const load = async () => {
      try {
        const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        if (isShipboardUser) {
          setVessels(list)
        } else {
          setVessels(list.filter((v: Vessel) => v.active ?? true))
        }
      } catch (error) {
        setVessels([])
        toast.error(formatAccessibleVesselError(error, 'Failed to load vessel list'), {
          position: 'top-center',
        })
      }
    }
    load()
  }, [isShipboardUser, currentUser])

  useEffect(() => {
    if (!isGlobalScope) return
    const load = async () => {
      try {
        const [cga, ca] = await Promise.all([getCompanyAdminList(), getCompanyList()])
        setCompanyAdmins(cga)
        setSubCompanies(ca)
      } catch { /* ignore */ }
    }
    load()
  }, [isGlobalScope])

  const defaultVesselId = isShipboardUser ? Number(currentUser?.vessel?.id ?? 0) : 0

  const currentReport = REPORTS.find((r) => r.key === activeReport) || REPORTS[0]
  const modalReport = REPORTS.find((r) => r.key === modalReportKey) || REPORTS[0]

  // Handler for direct download PDF from actions column
  const handleDownloadPdf = async (row: any) => {
    const vesselId = row.vesselId
    const month = row.reportMonth
    const year = row.reportYear

    if (!vesselId || !month || !year) {
      toast.error('Invalid report data', { position: 'top-center' })
      return
    }

    try {
      const params = { vesselId, month, year }

      const [ae, bp, cs, pu, ig, bw, fw] = await Promise.all([
        searchAuxiliaryEngineReports(params).catch(() => []),
        searchBoilerPerformanceReports(params).catch(() => []),
        searchCompressorSystemReports(params).catch(() => []),
        searchPurifierReports(params).catch(() => []),
        searchInertGasSystemReports(params).catch(() => []),
        searchBwtsReports(params).catch(() => []),
        searchFreshWaterGeneratorReports(params).catch(() => []),
      ])

      const vessel = vessels.find((v) => Number(v.id) === Number(vesselId))
      const vesselName = vessel?.fleet_name || vessel?.name || row.vesselName || `Vessel ${vesselId}`
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const period = `${monthNames[month - 1]} ${year}`

      generateMachineryStatusPdf({
        vesselName,
        period,
        auxiliaryEngine: ae[0] ?? null,
        boilerPerformance: bp[0] ?? null,
        compressorSystem: cs[0] ?? null,
        purifier: pu[0] ?? null,
        inertGasSystem: ig[0] ?? null,
        bwts: bw[0] ?? null,
        freshWaterGenerator: fw[0] ?? null,
      })

      toast.success('PDF downloaded successfully', { position: 'top-center' })
    } catch (err) {
      console.error('PDF generation failed:', err)
      toast.error('Failed to generate PDF', { position: 'top-center' })
    }
  }

  return accessDenied ? (
    <QhseAccessDenied message='Access denied â€” you do not have permission to view Machinery Reports.' />
  ) : (
    <>
      <ToastContainer />
      <div className='month-end-reports-wrapper d-flex flex-column gap-4' style={{minHeight: '75vh'}}>
        <div className='card'>
          <div className='card-body py-3 px-3 px-sm-4'>
            <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
              <div
                className='d-flex flex-nowrap align-items-stretch gap-3 gap-sm-4 overflow-x-auto flex-grow-1'
                style={{scrollbarWidth: 'thin'}}
              >
              <div className='d-flex align-items-center flex-shrink-0 py-1 px-2 px-sm-3 me-2 me-sm-3'>
                <h4 className='card-title fw-bold fs-5 m-0 text-nowrap'>
                  <i className='bi bi-gear me-2 text-body-secondary'></i>
                  Machinery Reports
                </h4>
              </div>
              {REPORTS.map((report) => (
                <button
                  key={report.key}
                  type='button'
                  className={`btn btn-sm flex-shrink-0 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-2 ${
                    activeReport === report.key
                      ? 'btn_primary'
                      : 'btn-light border border-secondary text-dark'
                  }`}
                  style={{
                    fontSize: '12px',
                    fontWeight: activeReport === report.key ? 600 : 500,
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => setActiveReport(report.key)}
                >
                  <i className={`bi ${report.icon} fs-6`}></i>
                  <span>{report.label}</span>
                </button>
              ))}
              </div>
              <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                {canAddMachineryReport && (
                  <button
                    type='button'
                    className='btn btn-sm btn_primary d-inline-flex align-items-center gap-2 px-3 py-2 rounded-2'
                    onClick={() => {
                      setModalSwitchSeed(null)
                      setModalReportKey(activeReport)
                      setAddRequest((prev) => ({id: prev.id + 1, seed: undefined}))
                    }}
                  >
                    <i className='bi bi-plus-lg fs-6'></i>
                    <span>Add Report</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='flex-grow-1' style={{minWidth: 0}}>
          <MachineryReportTable
            config={currentReport.config}
            modalConfig={modalReport.config}
            showAddButton={false}
            addRequestId={addRequest.id}
            addRequestSeed={addRequest.seed}
            activeReportKey={modalReportKey}
            addReportOptions={REPORTS.map((r) => ({key: r.key, label: r.label, icon: r.icon}))}
            reportRegistry={REPORTS.map((r) => ({
              key: r.key,
              searchFn: r.config.searchFn,
              createFn: r.config.createFn,
              updateFn: r.config.updateFn,
              defaultsFn: r.config.defaultsFn,
              emptyForm: r.config.emptyForm,
              repeatableSections: r.config.repeatableSections,
              title: r.config.title,
              carryForwardFields: r.config.carryForwardFields,
              carryForwardReplicaLabels: r.config.carryForwardReplicaLabels,
            }))}
            modalSwitchSeed={modalSwitchSeed ?? undefined}
            onSwitchAddReport={(reportKey: string, seed) => {
              setActiveReport(reportKey)
              setModalReportKey(reportKey)
              setModalSwitchSeed(seed ?? null)
            }}
            onRequestModalReportKey={(reportKey: string) => {
              setModalReportKey(reportKey)
            }}
            onDownloadPdf={handleDownloadPdf}
          />
        </div>
      </div>

      {/* PDF Download Modal - kept for potential future use */}
      <MachineryStatusPdfModal
        isOpen={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        canFilterCompanies={isGlobalScope}
        canRead={canRead}
        vessels={vessels}
        companyAdmins={companyAdmins}
        subCompanies={subCompanies}
        defaultVesselId={defaultVesselId || null}
      />
    </>
  )
}

export default MachineryStatusPage
